import React, { useEffect, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks'
import { createGetRequestsAreLoading, createGetConstantsByKey } from 'state'
import { STATE_KEYS } from 'state/requests'
import { CAMPAIGN_STATUS_ENUM, selectEditingCampaignCountry } from 'state/campaigns'
import { BrandState } from 'state/brands'
import { InputFieldContainer } from 'components/Shared/Fields/styles'
import Divider from 'components/Shared/Divider'
import InputField from 'components/Shared/Fields'
import ObjectiveSelector from './ObjectiveSelector'
import { selectCountrySetting } from 'state/constants'
import Text from 'particles/Text'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { usePermission, useShowPrivateCampaignBudget } from 'hooks/permissions'
import { FormikCheckboxGroup } from 'particles/Checkbox/Group'
import { fieldIdentifiers } from 'components/Shared/Comment'
import Checkbox from 'particles/Checkbox'
import Toggle from 'particles/Toggle'
import Modal from 'particles/Modal'
import { dateIsInPast } from 'services/dates'
import { TimeWindowState } from 'state/banners'
import { CampaignManagement } from 'components/Shared/Permission/enums'

interface CampaignObjectiveProps {
    campaignStatus?: CAMPAIGN_STATUS_ENUM
    campaignBrand?: BrandState
    campaignTimeWindow?: TimeWindowState
}

interface ObjectiveProps {
    id: number
    label: string
}

const CampaignObjective: React.FC<CampaignObjectiveProps> = ({ campaignStatus, campaignBrand, campaignTimeWindow }) => {
    const [t] = useNewTranslation()
    const { initialValues, values, setFieldValue } = useFormikContext<any>()
    const campaignConstants = useSelector(createGetConstantsByKey('campaign'))
    const isCampaignLive = Boolean(campaignStatus && campaignStatus === CAMPAIGN_STATUS_ENUM.LIVE)
    const isLoading = useSelector(createGetRequestsAreLoading([STATE_KEYS.Campaign]))
    const countrySetting = useSelector(selectCountrySetting(campaignBrand?.country || null))
    // editingCampaignCountry is used to provide faster country retrieval for number input budgetCurrencySign
    // because, waiting brandlist is took some time
    const editingCampaignCountry = useSelector(selectEditingCampaignCountry)
    const [isProfileRecommendationModalOpen, setIsProfileRecommendationModalOpen] = useState(false)
    const isEditProfileRecommendationAllowed = usePermission(CampaignManagement.AccessProfileRecommendation)

    const budgetCurrencySign = useMemo(() => {
        if (!campaignBrand?.country || !countrySetting) return editingCampaignCountry
        return countrySetting.currency.sign
    }, [editingCampaignCountry, campaignBrand, countrySetting])

    const showCampaignBudget = useShowPrivateCampaignBudget(values.is_private)

    useEffect(() => {
        if (!values.slot_required) {
            setFieldValue('slot_number', null)
        }
    }, [values.slot_required, setFieldValue])

    if (isLoading || campaignConstants === undefined) return null

    const { campaign_objections: objectives } = campaignConstants // FIXME: s/objections/objectives in backend
    const objectivesValid = objectives.filter((objective: ObjectiveProps) => objective.id > 0)
    const objectivesValidOptions = objectivesValid.map((objective: ObjectiveProps) => {
        return {
            value: objective.id,
            label: objective.label,
        }
    })
    const selectObjective = objectivesValid.find((objective: ObjectiveProps) => {
        return objective.id === values.objective
    })
    const categoriesOptions = campaignConstants.campaign_categories.map((category: ObjectiveProps) => ({
        value: category.id,
        label: category.label,
    }))

    const handleToggleProfileRecommendation = () => {
        const isValidTimeWindow =
            campaignTimeWindow &&
            editingCampaignCountry &&
            !dateIsInPast(campaignTimeWindow.end, editingCampaignCountry)
        if (isCampaignLive && !initialValues.profile_recommendation_enabled && !isValidTimeWindow) {
            setIsProfileRecommendationModalOpen(true)
        } else {
            setFieldValue('profile_recommendation_enabled', !Boolean(values.profile_recommendation_enabled))
        }
    }

    return (
        <>
            <InputFieldContainer>
                <CampaignEditorInputFieldContainer>
                    <InputField.TextInputField
                        name="title"
                        dataTestId="test-campaign-name"
                        showInputLength
                        maxInputLength={100}
                        label={t('campaignEditor.setUpCampaign.enterCampaignNameHeader')}
                        isRequired
                        identifier={fieldIdentifiers.campaignName}
                    />
                </CampaignEditorInputFieldContainer>
            </InputFieldContainer>

            <InputFieldContainer>
                <ObjectiveSelector options={objectivesValidOptions} value={selectObjective && selectObjective.id} />
            </InputFieldContainer>

            <InputFieldContainer>
                <CampaignEditorInputFieldContainer>
                    <FormikCheckboxGroup
                        name="categories"
                        dataTestId="test-category-checkbox"
                        title={t('campaignEditor.setUpCampaign.enterCampaignCategoryHeader')}
                        subtitle={t('campaignEditor.setUpCampaign.enterCampaignCategorySubtext')}
                        options={categoriesOptions}
                        orientation="horizontal"
                        identifier={fieldIdentifiers.categories}
                    />
                </CampaignEditorInputFieldContainer>
            </InputFieldContainer>

            {showCampaignBudget && (
                <InputFieldContainer>
                    <CampaignEditorInputFieldContainer>
                        <InputField.NumberInputField
                            title={t('campaignEditor.setUpCampaign.enterCampaignBudgetHeader')}
                            subtitle={t('campaignEditor.setUpCampaign.enterCampaignBudgetSubtext')}
                            name="budget"
                            data-testid="test-campaign-budget"
                            isRequired
                            placeholder="0.00"
                            decimal
                            currencySign={budgetCurrencySign}
                            identifier={fieldIdentifiers.budget}
                            country={campaignBrand?.country || editingCampaignCountry}
                        />
                    </CampaignEditorInputFieldContainer>
                </InputFieldContainer>
            )}
            <InputFieldContainer>
                <CampaignEditorInputFieldContainer>
                    {(isReadOnly: boolean) => (
                        <InputField.NumberInputField
                            title={t('campaign.metric.enterCampaignSlotsHeader')}
                            subtitle={t('campaign.metric.enterCampaignSlotsSubtext')}
                            name="slot_number"
                            data-testid="test-campaign-slot"
                            placeholder={values.slot_required ? '0' : undefined}
                            isRequired
                            min={1}
                            isDisable={!values.slot_required}
                            isReadOnly={isReadOnly}
                            identifier={fieldIdentifiers.slotNumber}
                            country={campaignBrand?.country || editingCampaignCountry}
                        />
                    )}
                </CampaignEditorInputFieldContainer>
                <CampaignEditorInputFieldContainer>
                    {(isReadOnly: boolean) =>
                        // in readonly view, we need to hide the checkbox when the value is true (unchecked)
                        isReadOnly && values.slot_required ? null : (
                            <Checkbox
                                label={t('campaignEditor.setUpCampaign.noCampaignSlotRequiredInstruction')}
                                onChange={() => {
                                    setFieldValue('slot_required', !values.slot_required)
                                }}
                                checkbox={values.slot_required ? 'notSelected' : 'selected'}
                                isReadOnly={isReadOnly}
                                size="m"
                            />
                        )
                    }
                </CampaignEditorInputFieldContainer>
            </InputFieldContainer>

            <Divider maxWidth="755px" />
            <InputFieldContainer>
                <CampaignEditorInputFieldContainer>
                    <Toggle
                        isChecked={values.is_private}
                        handleChange={() => {
                            const updateValue = !Boolean(values.is_private)
                            setFieldValue('is_private', updateValue)
                        }}
                        label={t('card.allCampaigns.privateCampaignLabel')}
                        subtext={t('campaignEditor.setUpCampaign.turnOnPrivateCampaignSubtext')}
                        isReadOnly={isCampaignLive ? isCampaignLive : undefined}
                        identifier={fieldIdentifiers.isPrivate}
                    />
                </CampaignEditorInputFieldContainer>
            </InputFieldContainer>
            <Divider maxWidth="755px" />
            <InputFieldContainer>
                <CampaignEditorInputFieldContainer>
                    <Toggle
                        isChecked={values.registration_enable}
                        handleChange={() => {
                            const updateValue = !Boolean(values.registration_enable)
                            setFieldValue('registration_enable', updateValue)
                            if (!updateValue) {
                                setFieldValue('registration_max_slots', null)
                                setFieldValue('profile_recommendation_enabled', false)
                            }
                        }}
                        label={t('campaignEditor.setUpCampaign.turnOnRegistrationHeader')}
                        subtext={t('campaignEditor.setUpCampaign.turnOnRegistrationSubtext')}
                        isReadOnly={isCampaignLive ? isCampaignLive : undefined}
                        dataTestId="registration-enable-toggle-input"
                        isDisabled={
                            (campaignStatus !== undefined &&
                                [CAMPAIGN_STATUS_ENUM.LIVE, CAMPAIGN_STATUS_ENUM.FOR_REVIEW].includes(
                                    campaignStatus,
                                )) ||
                            values.batching_enable
                        }
                        identifier={fieldIdentifiers.registrationEnable}
                    />
                </CampaignEditorInputFieldContainer>
                {values.registration_enable && (
                    <div className="register-input-area">
                        <CampaignEditorInputFieldContainer>
                            {(isReadOnly: boolean) => (
                                <>
                                    <InputField.NumberInputField
                                        title={t('campaignEditor.setUpCampaign.enterRegistrationSlotsHeader')}
                                        isRequired
                                        subtitle={t('campaignEditor.setUpCampaign.enterRegistrationSlotsSubtext')}
                                        name="registration_max_slots"
                                        data-testid="test-registration-max-slot"
                                        placeholder="0"
                                        country={campaignBrand?.country || editingCampaignCountry}
                                        footerText={
                                            values.slot_number
                                                ? t('campaignEditor.setUpCampaign.totalCampaignSlotsIndicator', {
                                                      count: values.slot_number,
                                                  })
                                                : undefined
                                        }
                                        identifier={fieldIdentifiers.registrationMaxSlots}
                                        isReadOnly={isReadOnly}
                                    />
                                    <Toggle
                                        isChecked={values.profile_recommendation_enabled}
                                        label={t('campaignEditor.setUpCampaign.turnOnSmartProfileTitle')}
                                        subtext={t('campaignEditor.setUpCampaign.turnOnSmartProfileSubtext')}
                                        handleChange={handleToggleProfileRecommendation}
                                        isReadOnly={
                                            isReadOnly ||
                                            (isCampaignLive && initialValues.profile_recommendation_enabled) ||
                                            !isEditProfileRecommendationAllowed
                                        }
                                        dataTestId="profile-recommendation-toggle-input"
                                        identifier={fieldIdentifiers.profileRecommendationEnabled}
                                    />
                                </>
                            )}
                        </CampaignEditorInputFieldContainer>
                    </div>
                )}
            </InputFieldContainer>
            <Divider maxWidth="755px" />
            <InputFieldContainer>
                <CampaignEditorInputFieldContainer>
                    <Toggle
                        isChecked={values.auto_update_submission_status}
                        handleChange={(): void => {
                            const updateValue = !Boolean(values.auto_update_submission_status)
                            setFieldValue('auto_update_submission_status', updateValue)
                            if (!updateValue) {
                                setFieldValue('batching_enable', false)
                            }
                        }}
                        label={t('campaignEditor.setUpCampaign.turnOnAutoStatusHeader')}
                        subtext={t('campaignEditor.setUpCampaign.turnOnAutoStatusSubtext')}
                        isReadOnly={isCampaignLive ? isCampaignLive : undefined}
                        identifier={fieldIdentifiers.autoUpdateSubmissionStatus}
                    />
                </CampaignEditorInputFieldContainer>
                {values.auto_update_submission_status && (
                    <div className="sub-input-field">
                        <div>
                            <Text scale="bodyM700" className="field-extra-bigger-title">
                                {t('campaignEditor.setUpCampaign.enterNumberSubmissionStatusUpdateHeader')}
                            </Text>
                            <Text className="field-sub-title " scale="bodyS400" color="secondary">
                                {t('campaignEditor.setUpCampaign.enterNumberSubmissionStatusUpdateSubtext')}
                            </Text>
                            <div className="auto-update-input-area">
                                <CampaignEditorInputFieldContainer>
                                    <InputField.NumberInputField
                                        title={t('campaign.metric.enterNumberSubmissionFullHeader')}
                                        isRequired
                                        subtitle={t('campaignEditor.setUpCampaign.enterNumberSubmissionFullSubtext')}
                                        name="submission_status_full_min"
                                        placeholder="0"
                                        country={campaignBrand?.country || editingCampaignCountry}
                                        footerText={
                                            values.slot_number
                                                ? t('campaignEditor.setUpCampaign.totalCampaignSlotsIndicator', {
                                                      count: values.slot_number,
                                                  })
                                                : undefined
                                        }
                                        identifier={fieldIdentifiers.submissionStatusFullMin}
                                    />
                                </CampaignEditorInputFieldContainer>
                            </div>
                            <div className="auto-update-input-area">
                                <CampaignEditorInputFieldContainer>
                                    <InputField.NumberInputField
                                        title={t('campaign.metric.enterNumberSubmissionFillingFastHeader')}
                                        isRequired
                                        subtitle={t(
                                            'campaignEditor.setUpCampaign.enterNumberSubmissionFillingFastSubtext',
                                        )}
                                        name="submission_status_filling_fast_min"
                                        placeholder="0"
                                        footerText={
                                            values.slot_number
                                                ? t('campaignEditor.setUpCampaign.totalCampaignSlotsIndicator', {
                                                      count: values.slot_number,
                                                  })
                                                : undefined
                                        }
                                        identifier={fieldIdentifiers.submissionStatusFillingFastMin}
                                    />
                                </CampaignEditorInputFieldContainer>
                            </div>
                            {values.batching_enable !== null && (
                                <CampaignEditorInputFieldContainer>
                                    <Toggle
                                        isChecked={values.batching_enable}
                                        handleChange={(): void => {
                                            const updateValue = !Boolean(values.batching_enable)
                                            setFieldValue('batching_enable', updateValue)
                                        }}
                                        label={t('campaignEditor.setUpCampaign.turnOnBatchSubmissionHeader')}
                                        subtext={t('campaignEditor.setUpCampaign.turnOnBatchSubmissionSubtext')}
                                        isReadOnly={isCampaignLive ? values.registration_enable : undefined}
                                        isDisabled={values.registration_enable}
                                        identifier={fieldIdentifiers.batchEnable}
                                    />
                                </CampaignEditorInputFieldContainer>
                            )}
                        </div>
                    </div>
                )}
            </InputFieldContainer>
            {isProfileRecommendationModalOpen && (
                <Modal
                    title={t('modal.unableToEnableSmartProfileRecommend.title')}
                    subtext={t('modal.unableToEnableSmartProfileRecommend.subtext')}
                    primaryButtonProps={{ label: t('helper.registration.accept.button.cta1') }}
                    size="m"
                    hasIcon
                    icon="warning"
                    onConfirm={() => setIsProfileRecommendationModalOpen(false)}
                    hasSecondaryButton={false}
                />
            )}
        </>
    )
}
export default CampaignObjective
